import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
  SelectPicker,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { MusicScheduleServices } from "../../../core/services/MusicScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { SongFields, SongListParams } from "../Songs/SongDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./MusicSchedules.css";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  genreTitle,
  getTodayShortDay,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import {
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  weekDaysObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import SingerCell from "../../components/Elements/SingerCell";
import { isEmpty } from "lodash";
import SongInfoCell from "../../components/Elements/SongInfoCell";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import MusicScheduleSongBulkAction from "./MusicScheduleSongBulkAction";
import { updateIn } from "json-immut";
import AudioPlayer from "../../components/Elements/AudioPlayer";
import { useMediaContext } from "../../../context/MediaContext";

function MusicScheduleSongs({
  musicScheduleId,
  withHeader = true,
  showPlayer = true,
  enableDayFilter = true,
  onDeleteSongs,
  enableBulkAction = [],
  songSearchParams = {
    smart_search: "",
    play_for_day: getTodayShortDay().toLowerCase(),
    time_slot: "",
  },
  ...props
}: {
  musicScheduleId: number;
  withHeader?: boolean;
  showPlayer?: boolean;
  enableDayFilter?: boolean;
  onDeleteSongs?: Function;
  enableBulkAction?: string[];
  songSearchParams?: any;
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [musicScheduleInfo, setMusicScheduleInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SongFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>(
    songSearchParams.smart_search
  );
  const [playForDaySearch, setPlayForDaySearch] = useState<string>(
    songSearchParams.play_for_day
  );
  const [timeSlotSearch, setTimeSlotSearch] = useState<string>(
    songSearchParams.time_slot
  );
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const initSearchFormData = {
    smart_search: songSearchParams.smart_search,
    time_slot: songSearchParams.time_slot,
    play_for_day: songSearchParams.play_for_day,
  };

  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.item_id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getMusicScheduleSongs(params: SongListParams) {
    try {
      if (isEmptyVal(musicScheduleId)) return;
      setLoading(true);
      const res = await MusicScheduleServices.getMusicScheduleSongs(
        musicScheduleId,
        params
      );
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getViewMusicScheduleDetail(id: number) {
    try {
      setLoadingSchedule(true);
      const res = await MusicScheduleServices.viewMusicSchedule(id);

      if (res && res.data && res.data.data) {
        setMusicScheduleInfo({ ...res.data.data });
      }
      setRows(res.data.data.total_songs);
      getMusicScheduleSongs(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: res.data.data.total_songs,
        })
      );
      setLoadingSchedule(false);
    } catch (error) {
      setLoadingSchedule(false);
    }
  }

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  async function init() {
    await getViewMusicScheduleDetail(musicScheduleId);
  }

  useEffect(() => {
    init();
  }, [musicScheduleId]);

  async function bulkDeleteAction() {
    try {
      let params = { item_ids: checkedKeys.join() };
      let res = await MusicScheduleServices.deleteSongsFromMusicSchedule(
        musicScheduleId,
        params
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });

      onDeleteSongs &&
        onDeleteSongs(musicScheduleId, {
          smart_search: smartSearchInput,
          time_slot: timeSlotSearch,
          play_for_day: playForDaySearch,
        });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ time_slot: timeSlotSearch },
      ...{ play_for_day: playForDaySearch },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: SongListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getMusicScheduleSongs(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const filterMusicScheduleSongs = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getMusicScheduleSongs(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setTimeSlotSearch("");
    if (enableDayFilter) setPlayForDaySearch(getTodayShortDay().toLowerCase());
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    getMusicScheduleSongs(
      getSearchParams({
        smart_search: "",
        time_slot: "",
        play_for_day: playForDaySearch,
        page: 1,
        sort_by: null,
        sort_order: null,
      })
    );
  };

  const deletePermission = UtilityServices.hasPermission("delete-song");
  const permissionObj: Permission = {
    delete: deletePermission,
  };

  return (
    <div className="music-schedule-songs">
      {loadingSchedule && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content="Loading..."
          center
          backdrop={true}
        />
      )}
      <Container className="container">
        {withHeader && (
          <Header className="h4">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={24}>
                <h4>
                  {t("MUSIC_SCHEDULE")} {musicScheduleInfo?.playlist_title}{" "}
                  {t("SONGS")}
                </h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content className="music-schedule-songs">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <div className="row music-schedule-song-controls-row">
                <MusicScheduleSongBulkAction
                  musicScheduleData={musicScheduleInfo}
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onDeleteSongs={bulkDeleteAction}
                  {...(enableBulkAction.length > 0 && { enableBulkAction })}
                />
                <div className="music-schedule-song-search">
                  <div className="music-schedule-song-smart-search-wrap">
                    <InputGroup className="music-schedule-song-smart-search">
                    {musicScheduleInfo?.new_schedule_everyday &&
                      enableDayFilter &&
                      musicScheduleInfo?.new_schedule_everyday.toString() ===
                        "1" && (
                        <SelectPicker
                          data={weekDaysObjArr}
                          valueKey="value"
                          labelKey="label"
                          onSelect={(value, item, e) => {
                            handleSearchFormChange({
                              path: "play_for_day",
                              value,
                            });
                            setPlayForDaySearch(value);
                            getMusicScheduleSongs(
                              getSearchParams({
                                play_for_day: value,
                              })
                            );
                          }}
                          onClean={(e: any) => {
                            handleSearchFormChange({
                              path: "play_for_day",
                              value: getTodayShortDay().toLowerCase(),
                            });
                            setPlayForDaySearch(
                              getTodayShortDay().toLowerCase()
                            );
                            getMusicScheduleSongs(
                              getSearchParams({
                                play_for_day: getTodayShortDay().toLowerCase(),
                              })
                            );
                          }}
                          name="play_for_day"
                          value={playForDaySearch}
                          placeholder={t("DAY")}
                        />
                      )}

                    <SelectPicker
                      data={musicScheduleInfo?.time_slots}
                      valueKey="id"
                      labelKey="slot_title"
                      onSelect={(value, item, e) => {
                        handleSearchFormChange({ path: "time_slot", value });
                        setTimeSlotSearch(value);
                        getMusicScheduleSongs(
                          getSearchParams({
                            time_slot: value,
                          })
                        );
                      }}
                      onClean={(e: any) => {
                        handleSearchFormChange({
                          path: "time_slot",
                          value: "",
                        });
                        setTimeSlotSearch("");
                        getMusicScheduleSongs(
                          getSearchParams({
                            time_slot: "",
                          })
                        );
                      }}
                      name="time_slot"
                      value={timeSlotSearch}
                      placeholder={t("DAY") + " " + t("PART")}
                    />
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("SONG").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("SINGER").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterMusicScheduleSongs(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterMusicScheduleSongs(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "song-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <h4>
                    {t("NO_ENTITY_FOUND_FOR", {
                      entity: t("SONG"),
                      for: musicScheduleInfo?.playlist_title,
                    })}
                  </h4>
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="item_id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                  uniqueId={"item_id"}
                />
              </Column>
              <Column width={200} sortable resizable flexGrow={1}>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CustomCell dataKey="title">
                  {(rowData: any) => (
                    <span title={`${rowData.title}`}>
                      {displayField(rowData.title)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={160}>
                <CustomHeaderCell>{t("SINGER")}</CustomHeaderCell>
                <SingerCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={80}>
                <CustomHeaderCell>{t("MORE")}</CustomHeaderCell>
                <SongInfoCell
                  rowData={(rowData: any) => rowData}
                  showData={[
                    "genre",
                    "era",
                    "mood",
                    "language",
                    "lyricist",
                    "composer",
                    "tempo",
                  ]}
                />
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>{t("TIME")}</CustomHeaderCell>
                <CustomCell dataKey="slot_title">
                  {(rowData: any) => (
                    <span>{displayField(rowData.slot_title)}</span>
                  )}
                </CustomCell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total"]}
            total={total}
          />
        </Footer>
        {showPlayer && <AudioPlayer />}

        <ToastContainer />
      </Container>
    </div>
  );
}

export default MusicScheduleSongs;
